import { Component, OnInit, OnDestroy } from "@angular/core";
import { TabCacheService } from "src/app/shared/tabs/tab-cache.service";
import { ConfigurationService } from "src/app/shared/configuration/configuration.service";
import { CloudUserStorageService } from "src/app/shared/user/cloud-user-storage.service";
import { FeatureService } from "src/app/shared/features/feature.service";
import {
  tap,
  takeWhile,
  finalize,
  map,
  mergeMap,
  toArray,
  catchError,
  filter,
} from "rxjs/operators";
import { Feature } from "src/app/shared/features/feature";
import {
  ActivatedRoute,
  Router,
  ChildActivationStart,
  ChildActivationEnd,
  ActivationEnd,
  NavigationExtras,
  UrlTree,
} from "@angular/router";
import { FeatureStorageService } from "src/app/shared/features/feature-storage.service";
import { of, from, throwError } from "rxjs";
import { RegionStorageService } from "src/app/shared/region/region-storage.service";
import { CloudIdStorageService } from "src/app/shared/cloud/cloud-id-storage.service";
import { ITabTemplate } from "src/app/shared/tabs/i-tab-template";
import { ITab } from "src/app/shared/tabs/i-tab";
import { TabTemplateCacheService } from "src/app/shared/tabs/tab-template-cache.service";
import { TabFactoryService } from "src/app/shared/tabs/tab-factory.service";
import { TAB_TEMPLATES } from "../tab-templates";
import { ActivatedTabService } from "src/app/shared/tabs/activated-tab.service";
import { TabSessionStorageService } from "src/app/shared/tabs/tab-session-storage.service";
import { TabSession } from "src/app/shared/tabs/tab-session";
import { TabTemplateFactoryService } from "src/app/shared/tabs/tab-template-factory.service";
import { CloudUserService } from "src/app/shared/user/cloud-user.service";
import { TabTemplateNameGeneratorService } from "src/app/shared/tabs/tab-template-name-generator.service";
import { Cloud, CloudService } from "src/app/shared/cloud/cloud.service";
import { CloudIsGovCloudStorageService } from "src/app/shared/cloud/cloud-is-gov-cloud-storage.service";
import { TermsAndConditions } from "src/app/shared/constants/terms-and-conditions";

@Component({
  standalone: false,
  selector: "app-features",
  templateUrl: "./features.component.html",
  styleUrls: ["./features.component.scss"],
})
export class FeaturesComponent implements OnInit, OnDestroy {
  get cachedTabs() {
    return this.tabCacheService.tabs;
  }

  get configuration() {
    return this.configurationService.configuration;
  }

  admin;

  get cachedEnabledStaticTabs() {
    return this.cachedTabs.filter((t) => {
      return t.enabled && t.static;
    });
  }

  get cachedTemplates() {
    return this.templateCacheService.getCachedTemplates();
  }

  get cachedStaticTabs() {
    return this.cachedTabs.filter((t) => t.static);
  }

  get tabs() {
    return this.tabSession.tabs;
  }

  get templates() {
    return this.tabSession.templates;
  }

  showConfirmationBanner = false;

  fedRampMessage: string = TermsAndConditions.FedRAMPConfirmationMessage;

  loading = false;

  alive = false;

  features: Feature[] = [];

  clouds: Cloud[] = [];

  error;

  get activeTab(): ITab {
    if (this.tabRoute) {
      return this.tabRoute.snapshot.data.tab;
    } else {
      return null;
    }
  }

  tabRoute: ActivatedRoute;

  get tabSession() {
    return this.tabSessionStorageService.tabSession;
  }
  set tabSession(v) {
    this.tabSessionStorageService.tabSession = v;
  }

  constructor(
    private tabCacheService: TabCacheService,
    private configurationService: ConfigurationService,
    private cloudUserStorageService: CloudUserStorageService,
    private featureService: FeatureService,
    private featureStorageService: FeatureStorageService,
    private router: Router,
    private cloudIdStorageService: CloudIdStorageService,
    private regionStorageService: RegionStorageService,
    private templateCacheService: TabTemplateCacheService,
    private tabFactoryService: TabFactoryService,
    private activatedTabService: ActivatedTabService,
    private activatedRoute: ActivatedRoute,
    private tabSessionStorageService: TabSessionStorageService,
    private tabTemplateFactoryService: TabTemplateFactoryService,
    private cloudUserService: CloudUserService,
    private templateNameGeneratorService: TabTemplateNameGeneratorService,
    private cloudIsGovCloudService: CloudIsGovCloudStorageService,
    private cloudService: CloudService
  ) {}

  ngOnDestroy() {
    this.alive = false;
  }

  loadRegion() {
    return this.regionStorageService.loadRegion();
  }

  getCachedTabById(tabId) {
    return this.tabCacheService.getTabById(tabId);
  }

  ngOnInit() {
    this.alive = true;

    this.loading = true;

    this.loadFeatures()
      .pipe(
        mergeMap(() => {
          const cachedCloudUser = this.getCachedCloudUser();

          if (cachedCloudUser) {
            this.admin = cachedCloudUser.IsSystemAdministrator;
            return of(cachedCloudUser);
          } else {
            return this.getCloudUser().pipe(
              tap((cloudUser) => {
                this.admin = cloudUser.IsSystemAdministrator;
                this.storeCloudUser(cloudUser);
              })
            );
          }
        }),
        tap(() => {
          if (!this.tabSession) {
            this.loadNewTabSession();
          }
        }),
        map(() => {
          return this.getActivatedTabRoot(this.activatedRoute);
        }),
        mergeMap((tabRoot) => {
          if (tabRoot) {
            return of(tabRoot);
          } else {
            const enabledTabs = this.tabs.filter((t) => t.enabled);

            if (enabledTabs.length) {
              return from(this.navigateByUrl(enabledTabs[0].url)).pipe(
                map(() => {
                  return null;
                })
              );
            } else {
              return of(null);
            }
          }
        }),
        tap(tabRoute => {
          this.tabRoute = tabRoute;
        }),
        filter(tabRoute => {
          return !!tabRoute;
        }),
        map(tabRoute => {
          return tabRoute.snapshot.data.tab;
        }),
        tap(tab => {
          const cachedTab = this.getCachedTabById(tab.id);
          if (!cachedTab) {
            this.cacheTab(tab);
          }

          if (!this.tabs.find((t) => t.id === tab.id)) {
            this.updateSessionInstance(tab);
            this.updateTabCaption(tab);
            this.tabs.push(tab);
          }

          if (tab.cloudId) {
            this.storeCloudId(tab.cloudId);
          }
          if (tab.region) {
            this.storeRegion(tab.region);
          }
        }),
        takeWhile(() => {
          return this.alive;
        }),
        finalize(() => {
          this.loading = false;
        }),
        catchError((e) => {
          if (e.message === "session_error") {
            this.tabSession = null;
          }
          return throwError(e);
        })
      )
      .subscribe();

    this.loadClouds().subscribe(x => {
      this.displayFedRAMPBanner();
    });
  }

  loadClouds() {
    return this.getClouds().pipe(
      tap((cloud) => {
        this.clouds.push(cloud);
      })
    );
  }

  getClouds() {
    return this.cloudService.getClouds();
  }

  displayFedRAMPBanner() {
    const cachedCloudId = this.cloudIdStorageService.loadCloudId();
    const cachedCloudIsGovCloud = this.cloudIsGovCloudService.loadCloudIsGovCloud();

    const cloud = this.clouds.find(c => c.Id == cachedCloudId);

    if (cloud != undefined) {
      if (cloud.IsGovCloud && cachedCloudIsGovCloud == null){
        this.showConfirmationBanner = true;
      }
    }
  }

  onClickYes() {
    this.cloudIsGovCloudService.storeCloudIsGovCloud(true);
    this.showConfirmationBanner = false;
  }

  navigateToClouds() {
    this.showConfirmationBanner = false;
    return this.router.navigateByUrl('/clouds');
  }

  updateTabCaption(tab: ITab) {
    tab.caption = this.getTabCaption(tab);
  }

  getTabCaption(tab: ITab) {
    const template = this.cachedTemplates.find(
      (t) => t.name === tab.templateName
    );

    return tab.sessionInstance && tab.sessionInstance > 1
      ? `${template.caption} (${tab.sessionInstance - 1})`
      : `${template.caption}`;
  }

  updateSessionInstance(tab: ITab) {
    tab.sessionInstance = this.getSessionInstanceNumber(tab.templateName);
  }

  getSessionInstanceNumber(templateName) {
    const ordered = this.tabs
      .filter((t) => t.templateName === templateName)
      .sort((t1, t2) => {
        if (t1.sessionInstance && t2.sessionInstance) {
          return t1.sessionInstance - t2.sessionInstance;
        } else {
          return 0;
        }
      });

    if (ordered.length) {
      return ordered[ordered.length - 1].sessionInstance + 1;
    } else {
      return 1;
    }
  }

  getCachedCloudUser() {
    return this.cloudUserStorageService.currentUser;
  }

  storeCloudUser(v) {
    this.cloudUserStorageService.currentUser = v;
  }

  getCloudUser() {
    return this.cloudUserService.getCloudUser();
  }

  closeTab(tab) {
    if (tab.id === this.getTabId()) {
      const prevTab = this.workoutPrevTab(tab);
      if (prevTab) {
        this.removeTabFromSession(tab);
        this.navigateByUrl(prevTab.url);
      } else {
        throw new Error("Could not find prev tab");
      }
    } else {
      this.removeTabFromSession(tab);
    }
  }

  removeTabFromSession(tab) {
    return this.tabSessionStorageService.removeTabFromSession(tab);
  }

  workoutPrevTab(tab) {
    const index = this.tabs.findIndex((t) => t.id === tab.id);
    if (index > -1) {
      return index === 0 ? this.tabs[index] : this.tabs[index - 1];
    } else {
      return null;
    }
  }

  navigateByUrl(url, extras?) {
    return this.router.navigateByUrl(url, extras);
  }

  getTabId() {
    return this.activatedTabService.getTabId(this.activatedRoute.snapshot);
  }

  getTab() {
    return this.activatedTabService.getTab(this.activatedRoute.snapshot);
  }

  loadNewTabSession() {
    this.tabSession = new TabSession();
    this.loadStaticTemplates();
    this.loadStaticTabs();
  }

  parseUrl(url: string) {
    return this.router.parseUrl(url);
  }

  featureEnabled(featureName) {
    return !!this.features.find((f) => f.Name === featureName);
  }

  loadCloudId() {
    return this.cloudIdStorageService.loadCloudId();
  }

  loadStaticTemplates() {
    for (const template of TAB_TEMPLATES.filter((t) => t.static)) {
      const cachedTemplate = this.cachedTemplates.find(
        (t) =>
          t.baseTemplateName === template.name &&
          t.cloudId === this.loadCloudId()
      );
      if (cachedTemplate) {
        this.templates.push(cachedTemplate);
      } else {
        const name = this.generateTemplateName(template.name);
        const childTemplate = this.createTemplate(template, name);
        if (
          (childTemplate.featureName &&
            !this.featureEnabled(childTemplate.featureName)) ||
          (childTemplate.admin && !this.admin)
        ) {
          childTemplate.enabled = false;
        }
        this.templates.push(childTemplate);
        if (this.shouldCacheTemplate(childTemplate)) {
          this.cachedTemplates.push(childTemplate);
        }
      }
    }
  }

  generateTemplateName(baseTemplateName: string) {
    return this.templateNameGeneratorService.generateNewTemplateName(
      baseTemplateName
    );
  }

  createTemplate(baseTemplate: ITabTemplate, name: string) {
    return this.tabTemplateFactoryService.createTemplate(baseTemplate, name);
  }

  shouldCacheTemplate(template: ITabTemplate): boolean {
    return !!this.getTemplateByName(template.name);
  }

  getTemplateByName(templateName) {
    return !this.templateCacheService.getTemplateByName(templateName);
  }

  loadStaticTabs() {
    for (const staticTemplate of this.templates.filter((t) => t.static)) {
      const tab = this.getStaticTab(staticTemplate);
      if (tab) {
        this.tabs.push(tab);
        if (this.shouldCacheTab(tab)) {
          this.cachedTabs.push(tab);
        }
      }
    }
  }

  getStaticTab(template: ITabTemplate) {
    const activeTab = this.getTab();
    if (activeTab && activeTab.templateName === template.name) {
      return activeTab;
    } else {
      const tabsOfTemplate = this.cachedTabs.filter(
        (t) => t.templateName === template.name
      );
      if (tabsOfTemplate.length) {
        return tabsOfTemplate[0];
      } else {
        return this.createTab(template);
      }
    }
  }

  shouldCacheTab(tab: ITab) {
    return !this.cachedTabs.find((t) => t.id === tab.id);
  }

  createTab(template) {
    return this.tabFactoryService.createTab(template);
  }

  cacheTab(tab) {
    this.cachedTabs.push(tab);
  }

  storeCloudId(cloudId) {
    return this.cloudIdStorageService.storeCloudId(cloudId);
  }

  storeRegion(region) {
    this.regionStorageService.storeRegion(region);
  }

  loadFeatures() {
    if (this.featureStorageService.loaded) {
      return of(this.loadFeaturesFromCache());
    } else {
      return this.getFeatures().pipe(
        tap((f) => {
          this.features.push(f);
          this.cacheFeature(f);
        }),
        toArray(),
        tap(() => {
          this.featureStorageService.loaded = true;
        })
      );
    }
  }

  cacheFeature(feature) {
    this.featureStorageService.storeFeature(feature);
  }

  loadFeaturesFromCache() {
    const cachedFeatures = this.getCachedFeatures();
    for (const feature of cachedFeatures) {
      this.features.push(feature);
    }
    return this.features;
  }

  getCachedFeatures() {
    return this.featureStorageService.loadFeatures();
  }

  getFeatures() {
    return this.featureService.getFeatures();
  }

  storeFeature(f) {
    return this.features.push(f);
  }

  onCloseTab(tab) {
    this.closeTab(tab);
  }

  onTabClick(tab) {
    const activeTabId = this.getTabId();
    if (tab.id !== activeTabId) {
      this.navigateByUrl(tab.url);
    }
  }

  getActivatedTabRoot(root: ActivatedRoute): ActivatedRoute {
    if (root.snapshot.data && root.snapshot.data.tab) {
      return root;
    } else if (root.firstChild) {
      return this.getActivatedTabRoot(root.firstChild);
    } else {
      return null;
    }
  }
}
